import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type PointerEvent } from 'react';
import { api, errorText } from '../../api';
import { currentLang, t } from '../../i18n';
import { useToast } from '../../lib/toast';
import { useClientApp } from '../context';

// Unterschreiben im Fenster eines Dokuments: Name, Unterschrift mit dem Finger (oder der Maus) und „gelesen“. Der Server
// macht daraus ein neues PDF mit Unterschriftsblatt; die Kanzlei bekommt es. Das Feld ist immer weiß mit dunkler
// Schrift, weil die Unterschrift so auf das Blatt kommt. Die Bestätigung „gelesen“ ist rechtlich verbindlich: Sie steht
// immer auf Deutsch da, in einer anderen Sprache darunter die Übersetzung mit dem Hinweis, dass der deutsche Text gilt.

const INK = '#14213d';

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export function SignForm({ docId, onDone, onCancel }: { docId: string; onDone(): void; onCancel(): void }) {
  const { me } = useClientApp();
  const toast = useToast();
  const canvas = useRef<HTMLCanvasElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const last = useRef<{ x: number; y: number } | null>(null);
  // Länge der Striche (CSS-Pixel) und Bereich, in dem gezeichnet wurde (Canvas-Pixel)
  const ink = useRef(0);
  const box = useRef<Box | null>(null);
  const [drawn, setDrawn] = useState(false);
  const [name, setName] = useState(me.user.name);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const touch = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

  useEffect(() => {
    form.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  // Auflösung an die Breite anpassen (scharf auf dem Handy). Ändert sich die Größe (Handy gedreht), ist das Feld leer.
  useLayoutEffect(() => {
    const c = canvas.current!;
    const fit = () => {
      const r = c.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const w = Math.round(r.width * dpr);
      const h = Math.round(r.height * dpr);
      if (!w || !h || (c.width === w && c.height === h)) return;
      c.width = w;
      c.height = h;
      ink.current = 0;
      box.current = null;
      setDrawn(false);
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(c);
    return () => ro?.disconnect();
  }, []);

  const scale = () => {
    const c = canvas.current!;
    return c.width / c.getBoundingClientRect().width;
  };
  const pos = (e: PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const stroke = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const c = canvas.current!;
    const ctx = c.getContext('2d')!;
    const s = scale();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 2.6 * s;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(a.x * s, a.y * s);
    ctx.lineTo(b.x * s, b.y * s);
    ctx.stroke();
    const pad = 3 * s;
    const bx = box.current ?? { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
    box.current = {
      x0: Math.min(bx.x0, a.x * s - pad, b.x * s - pad),
      y0: Math.min(bx.y0, a.y * s - pad, b.y * s - pad),
      x1: Math.max(bx.x1, a.x * s + pad, b.x * s + pad),
      y1: Math.max(bx.y1, a.y * s + pad, b.y * s + pad),
    };
    ink.current += Math.hypot(b.x - a.x, b.y - a.y);
    // ein Punkt oder ein kurzer Strich ist noch keine Unterschrift
    if (ink.current > 40 && !drawn) {
      setDrawn(true);
      setError(null);
    }
  };

  const down = (e: PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    last.current = pos(e);
    stroke(last.current, { x: last.current.x + 0.1, y: last.current.y });
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!last.current) return;
    e.preventDefault();
    const p = pos(e);
    stroke(last.current, p);
    last.current = p;
  };
  const up = () => {
    last.current = null;
  };
  const clear = () => {
    const c = canvas.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    ink.current = 0;
    box.current = null;
    setDrawn(false);
  };

  // Nur den Bereich mit der Unterschrift schicken (mit etwas Rand), mindestens 100 × 40 Pixel
  const signatureImage = () => {
    const c = canvas.current!;
    const b = box.current!;
    const m = 8 * scale();
    let x0 = Math.max(0, Math.floor(b.x0 - m));
    let y0 = Math.max(0, Math.floor(b.y0 - m));
    let x1 = Math.min(c.width, Math.ceil(b.x1 + m));
    let y1 = Math.min(c.height, Math.ceil(b.y1 + m));
    if (x1 - x0 < 100) [x0, x1] = [Math.max(0, x0 - 50), Math.min(c.width, x1 + 50)];
    if (y1 - y0 < 40) [y0, y1] = [Math.max(0, y0 - 20), Math.min(c.height, y1 + 20)];
    const out = document.createElement('canvas');
    out.width = Math.max(100, x1 - x0);
    out.height = Math.max(40, y1 - y0);
    out.getContext('2d')!.drawImage(c, x0, y0, x1 - x0, y1 - y0, 0, 0, x1 - x0, y1 - y0);
    return out.toDataURL('image/png');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 3) return setError(t('Bitte Ihren Vor- und Nachnamen eintragen.'));
    if (!drawn) return setError(t('Bitte im Feld unterschreiben.'));
    if (!ok) return setError(t('Bitte bestätigen, dass Sie das Dokument gelesen haben.'));
    setError(null);
    setBusy(true);
    try {
      await api.post(`/api/app/doc/${docId}/sign`, { name: name.trim(), signature: signatureImage(), confirm: true });
      toast(t('Unterschrieben · Ihre Kanzlei bekommt das unterschriebene Dokument'));
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <form ref={form} className="form maform dok-sign" onSubmit={submit} noValidate>
      <h4>{t('Unterschreiben')}</h4>
      <label>
        <span>
          {t('Ihr Vor- und Nachname')} <span className="req">*</span>
        </span>
        <input
          type="text"
          value={name}
          maxLength={80}
          autoComplete="name"
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
        />
      </label>
      <div className="fld">
        <span className="dok-pad-l" id="dok-pad-l">
          {t('Ihre Unterschrift')} <span className="req">*</span>
        </span>
        <div className="dok-pad">
          <canvas
            ref={canvas}
            role="img"
            aria-labelledby="dok-pad-l"
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            onPointerLeave={up}
          />
          {!drawn && (
            <span className="dok-pad-ph" aria-hidden="true">
              {touch ? t('Hier mit dem Finger unterschreiben') : t('Hier mit der Maus unterschreiben')}
            </span>
          )}
          <span className="dok-pad-line" aria-hidden="true" />
          <button type="button" className="dok-pad-x" onClick={clear}>
            {t('Löschen')}
          </button>
        </div>
      </div>
      <label className="chk">
        <input
          type="checkbox"
          checked={ok}
          onChange={(e) => {
            setOk(e.target.checked);
            setError(null);
          }}
        />
        <span>
          {/* verbindlich ist der deutsche Text, er bleibt immer sichtbar */}
          <span lang="de">Ich habe das Dokument gelesen und unterschreibe es elektronisch.</span>
          {currentLang() !== 'de' && (
            <small className="muted" style={{ display: 'block', marginTop: 4 }}>
              {t('Ich habe das Dokument gelesen und unterschreibe es elektronisch.')}{' '}
              <i>({t('Übersetzung zur Information. Verbindlich ist der deutsche Text.')})</i>
            </small>
          )}
        </span>
      </label>
      {error && (
        <p className="formerr" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn2 dark" type="submit" disabled={busy}>
          {busy ? t('Einen Moment …') : t('Jetzt unterschreiben')}
        </button>
        <button className="btn2" type="button" onClick={onCancel} disabled={busy}>
          {t('Abbrechen')}
        </button>
      </div>
      <div className="src">
        {t(
          'Ihre Unterschrift kommt mit Name, Datum und Uhrzeit auf ein Blatt am Ende des Dokuments. Ihre Kanzlei bekommt das unterschriebene PDF, Sie finden es danach auch hier.',
        )}
      </div>
    </form>
  );
}
